import React from 'react';
import { Box, Select, MenuItem, Button } from '@mui/material';
import { Track, Course, Batch } from './sessionTypes';
import { LIVE_SESSION_CATEGORY_OPTIONS } from '../../../../codelive/create/config/liveSessionTypes';

interface SessionFiltersProps {
  tracks: Track[];
  courses: Course[];
  batches: Batch[];
  selectedTrack: string;
  selectedCourse: string;
  selectedBatch: string;
  selectedCategory: string;
  loadingTracks: boolean;
  loadingCourses: boolean;
  loadingBatches: boolean;
  onTrackChange: (value: string) => void;
  onCourseChange: (value: string) => void;
  onBatchChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onExport?: () => void;
}

const selectSx = {
  minWidth: 180,
  height: 40,
  fontSize: 14,
  backgroundColor: '#fff',
  borderRadius: '8px',
  '& .MuiSelect-select': {
    py: 1,
    fontSize: 14,
    color: '#0f172a',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e2e8f0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#cbd5e1',
  },
};

const SessionFilters: React.FC<SessionFiltersProps> = ({
  tracks,
  courses,
  batches,
  selectedTrack,
  selectedCourse,
  selectedBatch,
  selectedCategory,
  loadingTracks,
  loadingCourses,
  loadingBatches,
  onTrackChange,
  onCourseChange,
  onBatchChange,
  onCategoryChange,
  onExport,
}) => {
  const trackLabel =
    tracks.find((t) => t.track_id === selectedTrack)?.track_name ||
    (selectedTrack ? (loadingTracks ? 'Loading...' : selectedTrack) : 'Select Track');
  const courseLabel =
    courses.find((c) => c.course_id === selectedCourse)?.course_name ||
    (selectedCourse ? (loadingCourses ? 'Loading...' : selectedCourse) : 'Select Course');
  const batchLabel =
    batches.find((b) => b.batch_id === selectedBatch)?.batch_name ||
    (selectedBatch ? (loadingBatches ? 'Loading...' : selectedBatch) : 'Select Batch');
  const categoryLabel =
    LIVE_SESSION_CATEGORY_OPTIONS.find(
      (o: { value: string; label: string }) => o.value === selectedCategory,
    )?.label || 'All session types';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'center',
        flexWrap: 'wrap',
        px: 0.5,
        py: 0.5,
        backgroundColor: 'transparent',
      }}
    >
      <Select
        value={selectedTrack}
        onChange={(e) => onTrackChange(e.target.value)}
        disabled={loadingTracks}
        displayEmpty
        variant="outlined"
        size="small"
        renderValue={() => (loadingTracks && !selectedTrack ? 'Loading...' : trackLabel)}
        sx={selectSx}
      >
        <MenuItem value="">Select Track</MenuItem>
        {selectedTrack && !tracks.some((t) => t.track_id === selectedTrack) ? (
          <MenuItem value={selectedTrack}>{trackLabel}</MenuItem>
        ) : null}
        {tracks.map((track) => (
          <MenuItem key={track.track_id} value={track.track_id}>
            {track.track_name}
          </MenuItem>
        ))}
      </Select>

      <Select
        value={selectedCourse}
        onChange={(e) => onCourseChange(e.target.value)}
        disabled={loadingCourses || !selectedTrack}
        displayEmpty
        variant="outlined"
        size="small"
        renderValue={() =>
          loadingCourses && !selectedCourse
            ? 'Loading...'
            : !selectedTrack
              ? 'Select Track First'
              : courseLabel
        }
        sx={selectSx}
      >
        <MenuItem value="">Select Course</MenuItem>
        {selectedCourse && !courses.some((c) => c.course_id === selectedCourse) ? (
          <MenuItem value={selectedCourse}>{courseLabel}</MenuItem>
        ) : null}
        {courses.map((course) => (
          <MenuItem key={course.course_id} value={course.course_id}>
            {course.course_name}
          </MenuItem>
        ))}
      </Select>

      <Select
        value={selectedBatch}
        onChange={(e) => onBatchChange(e.target.value)}
        disabled={loadingBatches || !selectedCourse}
        displayEmpty
        variant="outlined"
        size="small"
        renderValue={() =>
          loadingBatches && !selectedBatch
            ? 'Loading...'
            : !selectedCourse
              ? 'Select Course First'
              : batchLabel
        }
        sx={selectSx}
      >
        <MenuItem value="">Select Batch</MenuItem>
        {selectedBatch && !batches.some((b) => b.batch_id === selectedBatch) ? (
          <MenuItem value={selectedBatch}>{batchLabel}</MenuItem>
        ) : null}
        {batches.map((batch) => (
          <MenuItem key={batch.batch_id} value={batch.batch_id}>
            {batch.batch_name}
          </MenuItem>
        ))}
      </Select>

      <Select
        value={selectedCategory}
        onChange={(e) => onCategoryChange(e.target.value)}
        displayEmpty
        variant="outlined"
        size="small"
        renderValue={() => categoryLabel}
        sx={{ ...selectSx, minWidth: 220 }}
      >
        <MenuItem value="">All session types</MenuItem>
        {LIVE_SESSION_CATEGORY_OPTIONS.map(
          (opt: { value: string; label: string }) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>

      {onExport && (
        <Button
          variant="outlined"
          onClick={onExport}
          sx={{
            ml: 'auto',
            textTransform: 'none',
            borderRadius: '8px',
            borderColor: '#cbd5e1',
            color: '#334155',
            fontWeight: 600,
            px: 2,
            height: 40,
            backgroundColor: '#fff',
            '&:hover': {
              borderColor: '#94a3b8',
              backgroundColor: '#f8fafc',
            },
          }}
        >
          Export Excel
        </Button>
      )}
    </Box>
  );
};

export default SessionFilters;
